import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import currencies from '../constants/currencies.json';
import { isDemoUser, getDemoBusinessSelections, setDemoBusinessSelections } from '../utils/demoUtils';

export const BusinessContext = createContext();

const API_URL = process.env.REACT_APP_API_URL;

export const BusinessProvider = ({ children }) => {
  const location = useLocation();
  
  // State now holds an array of selected businesses
  const [selectedBusinesses, setSelectedBusinesses] = useState(() => {
    // Load initial state from localStorage as fallback
    const storedBusinesses = localStorage.getItem('selectedBusinesses');
    try {
      return storedBusinesses ? JSON.parse(storedBusinesses) : [];
    } catch (e) {
      localStorage.removeItem('selectedBusinesses');
      return [];
    }
  });
  const [currencySymbol, setCurrencySymbol] = useState(() => {
    // Load currency symbol from localStorage initially
    const storedSymbol = localStorage.getItem('currencySymbol');
    if (storedSymbol) {
      return storedSymbol;
    }
    // Default currency if nothing is stored
    return currencies['USD']?.symbol_native || '$';
  });
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [allBusinesses, setAllBusinesses] = useState([]); // Store all businesses for demo users

  // Function to fetch user info to determine if demo user
  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/users/`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUserEmail(userData.email);
        return userData.email;
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
    return '';
  }, []);

  // Function to fetch all businesses for demo users
  const fetchAllBusinesses = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/businesses/`, { credentials: 'include' });
      if (response.ok) {
        const businesses = await response.json() || [];
        setAllBusinesses(businesses);
        return businesses;
      }
    } catch (error) {
      console.error('Error fetching all businesses:', error);
    }
    return [];
  }, []);

  // Function to auto-select first MONITORED business for demo users if none selected
  const autoSelectFirstBusiness = useCallback((businesses) => {
    if (businesses && businesses.length > 0) {
      // Filter for monitored businesses only
      const monitoredBusinesses = businesses.filter(business => business.is_monitored === true);
      
      if (monitoredBusinesses.length > 0) {
        const firstMonitoredBusiness = monitoredBusinesses[0];
        setSelectedBusinesses([firstMonitoredBusiness]);
        setDemoBusinessSelections([firstMonitoredBusiness]);
        return [firstMonitoredBusiness];
      }
    }
    return [];
  }, []);

  // Function to fetch selected businesses from backend (for regular users)
  const fetchSelectedBusinessesFromBackend = useCallback(async () => {
    setIsLoadingBusinesses(true);
    setFetchError(null);
    
    try {
      const response = await fetch(`${API_URL}/businesses/`, { credentials: 'include' });
      if (!response.ok) {
        if (response.status === 401) {
          // User not authenticated, clear selections
          setSelectedBusinesses([]);
          localStorage.removeItem('selectedBusinesses');
          return { selectedFromBackend: [], allBusinesses: [] };
        }
        throw new Error('Failed to fetch businesses');
      }
      
      const businesses = await response.json() || [];
      // Extract businesses that are selected in the backend
      const selectedFromBackend = businesses.filter(business => business.is_selected === true);
      
      // Update state and localStorage with backend data
      setSelectedBusinesses(selectedFromBackend);
      localStorage.setItem('selectedBusinesses', JSON.stringify(selectedFromBackend));
      
      return { selectedFromBackend, allBusinesses: businesses };
      
    } catch (error) {
      console.error('Error fetching selected businesses from backend:', error);
      setFetchError(error.message);
      // Keep current state as fallback, don't clear on error
      return { selectedFromBackend: [], allBusinesses: [] };
    } finally {
      setIsLoadingBusinesses(false);
    }
  }, []);

  // Function to handle demo user business selection from query parameters
  const loadDemoBusinessFromQuery = useCallback(async (businessIds) => {
    setIsLoadingBusinesses(true);
    
    try {
      // Fetch all businesses first
      const businesses = await fetchAllBusinesses();
      
      // Parse business IDs from query parameter
      const ids = businessIds.split(',').map(id => id.trim());
      
      // Find businesses that match the IDs and are monitored
      const matchingBusinesses = businesses.filter(biz => 
        ids.includes(biz.id) && biz.is_monitored === true
      );
      
      if (matchingBusinesses.length > 0) {
        setSelectedBusinesses(matchingBusinesses);
        // Save to demo localStorage so it persists if user navigates away
        setDemoBusinessSelections(matchingBusinesses);
      } else {
        // If no matching businesses found, fall back to auto-select
        autoSelectFirstBusiness(businesses);
      }
    } catch (error) {
      console.error('Error loading demo businesses from query:', error);
      // Fall back to auto-select on error
      const businesses = await fetchAllBusinesses();
      autoSelectFirstBusiness(businesses);
    }
    
    setIsLoadingBusinesses(false);
  }, [fetchAllBusinesses, autoSelectFirstBusiness]);

  // Function to handle demo user business selection loading
  const loadDemoBusinessSelections = useCallback(async () => {
    setIsLoadingBusinesses(true);
    
    // Check for query parameters first (for demo links)
    const searchParams = new URLSearchParams(location.search);
    const businessIds = searchParams.get('businesses');
    
    if (businessIds) {
      // Load businesses from query parameters
      await loadDemoBusinessFromQuery(businessIds);
      return;
    }
    
    // Fetch all businesses first
    const businesses = await fetchAllBusinesses();
    
    // Get current demo selections from localStorage
    const demoSelections = getDemoBusinessSelections();
    
    if (demoSelections.length > 0) {
      // Use existing demo selections, but validate they still exist and are monitored
      const validSelections = demoSelections.filter(selected => 
        businesses.some(biz => biz.id === selected.id && biz.is_monitored === true)
      );
      
      if (validSelections.length > 0) {
        setSelectedBusinesses(validSelections);
      } else {
        // Auto-select first monitored business if saved selections are no longer valid/monitored
        autoSelectFirstBusiness(businesses);
      }
    } else {
      // No saved selections, auto-select first monitored business
      autoSelectFirstBusiness(businesses);
    }
    
    setIsLoadingBusinesses(false);
  }, [fetchAllBusinesses, autoSelectFirstBusiness, location.search, loadDemoBusinessFromQuery]);

  // Check authentication status and fetch selected businesses when authenticated
  useEffect(() => {
    const checkAuthAndFetchSelections = async () => {
      try {
        const authResponse = await fetch(`${API_URL}/users/`, { credentials: 'include' });
        if (authResponse.ok) {
          const userData = await authResponse.json();
          setUserEmail(userData.email);
          
          if (isDemoUser(userData.email)) {
            // Demo user - use local selections
            await loadDemoBusinessSelections();
          } else {
            // Regular user - fetch from backend
            await fetchSelectedBusinessesFromBackend();
          }
        } else {
          // User not authenticated, clear selections
          setSelectedBusinesses([]);
          localStorage.removeItem('selectedBusinesses');
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Keep current localStorage state as fallback
      }
    };

    checkAuthAndFetchSelections();
  }, []); // Run once on mount

  // Auto-select business when navigating to pages that require business data
  useEffect(() => {
    // Pages that require business data
    const pagesRequiringBusiness = ['/', '/reviews', '/timeline', '/settings/custom-categories'];
    const currentPageRequiresBusiness = pagesRequiringBusiness.includes(location.pathname);
    
    // Only auto-select if:
    // 1. Current page requires business data
    // 2. No business is currently selected
    // 3. User email is set (authentication has completed)
    // 4. Not currently loading businesses
    if (currentPageRequiresBusiness && 
        selectedBusinesses.length === 0 && 
        userEmail && 
        !isLoadingBusinesses) {
      
      if (isDemoUser(userEmail)) {
        // Auto-select first business for demo users
        loadDemoBusinessSelections();
      } else {
        // For regular users, fetch their businesses and auto-select first if none are selected
        fetchSelectedBusinessesFromBackend().then(async (result) => {
          const { selectedFromBackend, allBusinesses } = result;
          
          // If no businesses selected after fetching from backend
          if (selectedFromBackend.length === 0 && allBusinesses.length > 0) {
            try {
              // Filter for monitored businesses only
              const monitoredBusinesses = allBusinesses.filter(business => business.is_monitored === true);
              
              if (monitoredBusinesses.length > 0) {
                // Auto-select the first monitored business
                const firstMonitoredBusiness = monitoredBusinesses[0];
                const selectResponse = await fetch(`${API_URL}/user-business-association/${firstMonitoredBusiness.id}/select`, {
                  method: 'PUT',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ is_selected: true }),
                });
                
                if (selectResponse.ok) {
                  // Update local state
                  setSelectedBusinesses([firstMonitoredBusiness]);
                  localStorage.setItem('selectedBusinesses', JSON.stringify([firstMonitoredBusiness]));
                }
              }
              // If no monitored businesses, don't auto-select anything
              // User will be handled by existing redirect logic elsewhere
            } catch (error) {
              console.error('Error auto-selecting business for regular user:', error);
            }
          }
        }).catch(error => {
          console.error('Error fetching selected businesses:', error);
        });
      }
    }
  }, [location.pathname, selectedBusinesses.length, userEmail, isLoadingBusinesses, loadDemoBusinessSelections, fetchSelectedBusinessesFromBackend]);

  // Update currency symbol based on the FIRST selected business
  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      const firstBusiness = selectedBusinesses[0];
      if (firstBusiness && firstBusiness.currency) {
        const currencyData = currencies[firstBusiness.currency];
        const symbol = currencyData ? currencyData.symbol_native : '';
        setCurrencySymbol(symbol);
        localStorage.setItem('currencySymbol', symbol); // Store derived symbol
      } else {
        // Fallback if first business has no currency
        const defaultSymbol = currencies['USD']?.symbol_native || '$';
        setCurrencySymbol(defaultSymbol);
        localStorage.setItem('currencySymbol', defaultSymbol);
      }
    } else {
      // No businesses selected, check localStorage first, then default
      const storedSymbol = localStorage.getItem('currencySymbol');
      if (storedSymbol) {
        setCurrencySymbol(storedSymbol);
      } else {
        const defaultSymbol = currencies['USD']?.symbol_native || '$';
        setCurrencySymbol(defaultSymbol);
        // Don't save default back to localStorage if no businesses are selected
      }
    }
  }, [selectedBusinesses]);

  // Function to update the array of selected businesses and store appropriately
  const updateSelectedBusinesses = useCallback((businesses) => {
    // Ensure businesses is always an array
    const businessesArray = Array.isArray(businesses) ? businesses : [];
    setSelectedBusinesses(businessesArray);
    
    if (isDemoUser(userEmail)) {
      // Demo user - save to demo localStorage only
      setDemoBusinessSelections(businessesArray);
    } else {
      // Regular user - save to regular localStorage
      localStorage.setItem('selectedBusinesses', JSON.stringify(businessesArray));
    }
  }, [userEmail]);

  // Function to clear selected businesses
  const clearSelectedBusinesses = useCallback(() => {
    updateSelectedBusinesses([]); // Pass empty array
  }, [updateSelectedBusinesses]);

  // Function to refresh selections from backend (or reload demo selections)
  const refreshSelectedBusinesses = useCallback(async () => {
    if (isDemoUser(userEmail)) {
      return loadDemoBusinessSelections();
    } else {
      return fetchSelectedBusinessesFromBackend();
    }
  }, [userEmail, loadDemoBusinessSelections, fetchSelectedBusinessesFromBackend]);

  // Memoize the context value
  const contextValue = useMemo(() => ({
    selectedBusinesses, // Use the new state variable
    updateSelectedBusinesses, // Use the new update function
    currencySymbol,
    // setCurrencySymbol is stable, no need to include
    clearSelectedBusinesses, // Use the new clear function
    refreshSelectedBusinesses, // New function to refresh from backend
    isLoadingBusinesses, // Expose loading state
    fetchError, // Expose error state
    userEmail, // Expose user email for demo detection
    allBusinesses, // Expose all businesses for demo users
    isDemoMode: isDemoUser(userEmail) // Expose demo mode status
  }), [
    selectedBusinesses,
    updateSelectedBusinesses,
    currencySymbol,
    clearSelectedBusinesses,
    refreshSelectedBusinesses,
    isLoadingBusinesses,
    fetchError,
    userEmail,
    allBusinesses
  ]);

  return (
    <BusinessContext.Provider value={contextValue}>
      {children}
    </BusinessContext.Provider>
  );
};
